import { formatCodeableConcept, formatReferenceString, type WithId } from '@medplum/core';
import type { Encounter } from '@medplum/fhirtypes';
import { format, parseISO } from 'date-fns';
import { ChevronRightIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@liza/ui/utils';
import { DATE_FORMATS } from '../../utils/date';
import { Item, ItemActions, ItemContent, ItemDescription, ItemFooter, ItemTitle } from '../ui/item';
import { type PromotionStatus, RecordPromotionButton } from './RecordPromotionButton';

export type EncountersSummaryProps = {
  encounters: WithId<Encounter>[];
  onViewAll?: () => void;
  onSelectEncounter?: (encounterId: string) => void;
  selectedEncounterId?: string;
  onPromotionClick?: (id: string) => void;
  getPromotionStatus?: (id: string) => PromotionStatus;
};

const EncounterCard = ({
  encounter,
  onClick,
  onPromotionClick,
  promotionStatus,
  selected,
}: {
  encounter: WithId<Encounter>;
  onClick?: () => void;
  onPromotionClick?: () => void;
  promotionStatus?: PromotionStatus;
  selected?: boolean;
}): React.ReactElement | null => {
  const { t } = useTranslation('records');
  const type = formatCodeableConcept(encounter.type?.at(0)) || t('unknownEncounter');
  const date = encounter.period?.start ? format(parseISO(encounter.period.start), DATE_FORMATS.DATE) : t('unknown');
  const location = formatReferenceString(encounter.location?.at(0)?.location);
  const reasonCodes = encounter.reasonCode?.map((r) => formatCodeableConcept(r)).filter(Boolean);

  const content = (
    <>
      <ItemContent>
        <ItemTitle className="font-medium">
          {type} - {reasonCodes?.join(', ')}
        </ItemTitle>
        <ItemDescription
          className={cn('text-xs', onClick && selected ? 'text-accent-foreground/70' : 'text-muted-foreground')}
        >
          {date}
        </ItemDescription>
        {location && (
          <ItemDescription
            className={cn('text-xs', onClick && selected ? 'text-accent-foreground/70' : 'text-muted-foreground')}
          >
            {location}
          </ItemDescription>
        )}
      </ItemContent>
      <ItemActions>
        {onPromotionClick && <RecordPromotionButton onClick={onPromotionClick} status={promotionStatus} />}
        {onClick && <ChevronRightIcon className="size-4 text-muted-foreground" />}
      </ItemActions>
    </>
  );

  if (onClick) {
    return (
      <Item
        size="sm"
        className={cn(
          'cursor-pointer hover:bg-accent/50',
          selected && 'bg-accent text-accent-foreground hover:bg-accent',
        )}
        onClick={onClick}
        aria-current={selected ? 'true' : undefined}
        data-testid="encounter-list-item"
      >
        {content}
      </Item>
    );
  }

  return (
    <Item size="sm" data-testid="encounter-list-item">
      {content}
    </Item>
  );
};

// oxlint-disable-next-line react/no-multi-comp
export const EncountersSummary = ({
  encounters,
  onViewAll,
  onSelectEncounter,
  selectedEncounterId,
  onPromotionClick,
  getPromotionStatus,
}: EncountersSummaryProps): React.ReactElement | null => {
  const { t } = useTranslation('records');

  if (onViewAll && encounters.length === 0) {
    return null;
  }

  // If onViewAll is provided, show a single summary card
  if (onViewAll && !onSelectEncounter) {
    const totalEncounters = encounters.length;
    const mostRecent = encounters[0];

    return (
      <Item className="cursor-pointer" onClick={onViewAll}>
        <ItemContent>
          <ItemTitle className="text-lg font-medium">{t('encounters')}</ItemTitle>
          <ItemDescription className="text-xs text-muted-foreground">
            {totalEncounters} {t('onRecord')}
          </ItemDescription>
        </ItemContent>
        <ItemActions>
          <ChevronRightIcon className="size-4 text-muted-foreground" />
        </ItemActions>
        {mostRecent && (
          <ItemFooter className="justify-start">
            <span className="text-muted-foreground">{t('mostRecentLabel')}</span>
            <span className="font-medium">
              {formatCodeableConcept(mostRecent.type?.at(0)) || t('unknownEncounter')}
            </span>
            <span className="text-muted-foreground">
              {' '}
              {t('onDate', {
                date: mostRecent.period?.start
                  ? format(parseISO(mostRecent.period.start), DATE_FORMATS.DATE)
                  : t('unknown'),
              })}
            </span>
          </ItemFooter>
        )}
      </Item>
    );
  }

  // Show individual encounter cards
  return (
    <div>
      {encounters.map((encounter) => (
        <EncounterCard
          key={encounter.id}
          encounter={encounter}
          onClick={onSelectEncounter ? (): void => onSelectEncounter(encounter.id) : undefined}
          selected={selectedEncounterId === encounter.id}
          onPromotionClick={onPromotionClick ? (): void => onPromotionClick(encounter.id) : undefined}
          promotionStatus={getPromotionStatus?.(encounter.id)}
        />
      ))}
    </div>
  );
};
